import parameterize from 'parameterize';
import { visit } from 'unist-util-visit';

const filterByNumber = (str) => str.replace(/[^\d]/g, '');

const yearsRegEx = [
  /^\d{4}$/, // Matches "1998"
  /^\d{4}s?$/i, // Matches "1998s" or "1970s" (optional 's')
  /^\d{4}(s)?-\d{4}(s)?$/i, // Matches "1970-1980", "1970s-1980s", "1970s-1980", "1970-1980s"
  /^(Early|Late)\s\d{4}s?$/i, // Matches "Early 1970s", "Late 1970s", "Early 1970", "Late 1970"
];

export const makeYearHeading =
  (headingTagName = 'h3') =>
  () =>
  (tree) => {
    const nodesToTransform = [];
    visit(tree, 'text', (node, index, parent) => {
      if (
        typeof node.value === 'string' &&
        yearsRegEx.some((regEx) => regEx.test(node.value.trim()))
      ) {
        const isBlockElement =
          parent?.type === 'element' &&
          ['p', 'div', 'section'].includes(parent.tagName);
        const hasBreakBefore =
          isBlockElement ||
          parent?.children?.some((child) => child.tagName === 'br');

        if (hasBreakBefore) {
          nodesToTransform.push(node);
        }
      }
    });

    nodesToTransform.forEach((node) => {
      const textNode = { type: 'text', value: node.value.trim() };

      node.type = 'element';
      node.tagName = headingTagName;
      node.children = [textNode];
      node.properties = {
        class: 'year-heading',
      };
      delete node.value;
    });
  };

export const clearHeadingFromChildrenTags =
  (headingTagName) => () => (tree) => {
    visit(tree, 'element', (node) => {
      if (node.tagName === headingTagName) {
        if (node.children[0].children?.[0]?.type === 'text') {
          node.children = node.children[0].children;
        }
      }
    });
  };

const getTextNode = (node) => {
  if (!node || node.type === 'text' || typeof node.value === 'string') {
    return node;
  }

  return getTextNode(node.children[0]);
};

const getText = (node) => {
  if (!node || node.type === 'text' || typeof node.value === 'string') {
    return node.value.trim();
  }

  return node?.children?.map((child) => getText(child)).join('') || '';
};

export const createTOC =
  ({
    setTOC,
    headingTagNames = ['h2', 'h3'],
    normalize = true,
    defaultMinLevel = 2,
    tocIndex = 0,
  }) =>
  () =>
  (tree) => {
    const toc = [];
    let minLevel = defaultMinLevel;
    visit(tree, 'element', (node) => {
      if (headingTagNames.includes(node.tagName)) {
        const text = getText(node);
        if (text && !node.value) {
          const id = `${parameterize(text)}-${toc.length + tocIndex}`;
          node.properties.id = id;
          const nodeLevel = +filterByNumber(node.tagName);
          minLevel = Math.min(minLevel, nodeLevel);
          toc.push({
            id,
            level: nodeLevel,
            title: text,
          });
        }
      }
    });

    setTOC(
      normalize
        ? toc.map((item) => ({ ...item, level: item.level - minLevel }))
        : [...toc],
    );
  };

export const shiftHeading =
  (headingTagNames = ['h2']) =>
  () =>
  (tree) => {
    visit(tree, 'element', (node) => {
      if (headingTagNames.includes(node.tagName)) {
        node.tagName = `h${+node.tagName[1] + 1}`;
      }
    });
  };

export const groupBySection =
  (headingTagName, skipFirstSection) => () => (tree) => {
    const updatedTree = tree.children.reduce(
      (acc, node) => {
        const lastIndex = acc.length - 1;
        if (node.tagName === headingTagName) {
          return [
            ...acc,
            {
              type: 'element',
              tagName: 'section',
              properties: {
                class: 'with-heading',
              },
              children: [
                node,
                {
                  type: 'element',
                  tagName: 'div',
                  children: [],
                },
              ],
            },
          ];
        } else if (acc[lastIndex].children[1]) {
          acc[lastIndex].children[1].children.push(node);
        } else {
          acc[lastIndex].children.push(node);
        }
        return acc;
      },
      [
        {
          type: 'element',
          tagName: 'section',
          properties: {
            id: 'first-section',
          },
          children: [],
        },
      ],
    );

    return {
      ...tree,
      children: skipFirstSection ? updatedTree.slice(1) : updatedTree,
    };
  };
